import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export type UseArchiveJobLevelsParams = {
    readonly jobLevels: NonEmptyArray<JobLevel | JobLevelDetails>;
};

type ArchiveJobLevelsContext = {
    readonly architectureId: string;
};

export type UseArchiveJobLevelsResult = UseMutationResult<
    unknown,
    Error,
    UseArchiveJobLevelsParams,
    ArchiveJobLevelsContext
>;

export const useArchiveJobLevels = (): UseArchiveJobLevelsResult => {
    const { archiveJobLevels } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ jobLevels }: UseArchiveJobLevelsParams) => {
            const { architectureId } = jobLevels[0];
            const [first, ...rest] = jobLevels;
            const jobLevelIds: NonEmptyArray<string> = [first.id, ...rest.map((level) => level.id)];

            const result = await archiveJobLevels({
                jobArchitectureId: architectureId,
                jobLevelIds,
            });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: (params): ArchiveJobLevelsContext => {
            const { architectureId } = params.jobLevels[0];
            const levelsByRole = new Map<string, (JobLevel | JobLevelDetails)[]>();

            params.jobLevels.forEach((level) => {
                if (level.parentId !== null) {
                    const roleId = level.parentId;
                    if (!levelsByRole.has(roleId)) {
                        levelsByRole.set(roleId, []);
                    }
                    levelsByRole.get(roleId)!.push(level);
                }
            });

            levelsByRole.forEach((levelsToArchive, roleId) => {
                queryClient.cancelQueries({
                    queryKey: jobArchitectureKeys.levelsByRole(architectureId, roleId),
                });

                queryClient.setQueryData(
                    jobArchitectureKeys.levelsByRole(architectureId, roleId),
                    (old: readonly JobLevel[] | undefined) => {
                        return old?.filter((jobLevel) => !levelsToArchive.some((l) => l.id === jobLevel.id));
                    },
                );
            });

            return { architectureId };
        },
        onSuccess: (_, __, context) => {
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architecture(context.architectureId),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architectureDetail(context.architectureId),
            });
        },
        throwOnError: false,
    });
};
